import { isChildRole, isParentRole, isParentUser } from 'src/utils/userRole';
import { isNative, getPlatform, isIos } from 'src/utils/platform';
import { useCurrentUser } from 'src/queries/user';
import {
  initAppsFlyer,
  loginAppsFlyer,
  onAppsFlyerUidResolved,
} from 'src/modules/AppsFlyer/AppsFlyer.module';
import { useEffect, useRef } from 'react';
import { User, UserType } from 'src/types/user.types';
import { reportError } from 'src/modules/logs/Sentry';
import { getATTStatus } from 'src/modules/analytics/ATT';
import { Device } from '@capacitor/device';
import { useSearchParams } from 'react-router-dom';
import { Purchases } from '@revenuecat/purchases-capacitor';
import {
  enableFacebookTracking,
  disableFacebookTracking,
} from 'src/modules/analytics/FacebookSDK';
import { setIsParentUser } from 'src/modules/analytics/analytics';
import {
  collectAttributionPayload,
  isSameAttribution,
  type AttributionPayload,
} from 'src/modules/AppsFlyer/attribution';
import { useReportAttribution } from 'src/queries/attribution';

type ReportAttribution = (payload: AttributionPayload) => Promise<void>;

const configureTracking = async (
  userType: UserType,
  isStale: () => boolean,
) => {
  const isParent = isParentRole(userType);
  setIsParentUser(isParent);

  if (!isParent) {
    await disableFacebookTracking();
    await initAppsFlyer(userType);
    return;
  }

  let attAuthorized = true;
  if (isIos()) {
    const attStatus = await getATTStatus();
    attAuthorized = attStatus === 'authorized';
  }
  if (isStale()) return;
  await enableFacebookTracking(attAuthorized);
  if (isStale()) return;
  await initAppsFlyer(userType);
};

const reportAttributionIfParent = async (
  user: User,
  uid: string,
  report: ReportAttribution,
) => {
  if (!isParentUser(user) || !uid) return;
  // Web builds never have a native AppsFlyer UID to begin with (uid would be '' and we'd already
  // have returned above), but keep this ordered first: skip the native-only lookups before touching
  // getATTStatus/Device.getInfo.
  if (!isNative()) return;

  const platform = getPlatform();
  const [attStatus, info] = await Promise.all([
    platform === 'ios' ? getATTStatus() : Promise.resolve(null),
    Device.getInfo().catch(() => null),
  ]);
  const payload = collectAttributionPayload({
    uid,
    platform,
    attStatus,
    osVersion: info?.osVersion ?? null,
  });
  if (!payload) return;

  try {
    await report(payload);
  } catch (error) {
    reportError('Failed to report attribution', error);
  }
};

const loginUser = async (user: User, isStale: () => boolean) => {
  await loginAppsFlyer(user);
  if (isStale()) return;
};

const AppsFlyer = () => {
  const { data: user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const userTypeParam = searchParams.get('user_type');
  const { mutateAsync: reportAttribution } = useReportAttribution();

  const userType =
    user?.user_type ??
    (isParentRole(userTypeParam)
      ? UserType.parent
      : isChildRole(userTypeParam)
        ? UserType.child
        : undefined);

  const configuredRef = useRef<{ userType?: UserType; userId?: number }>({});
  const versionRef = useRef(0);
  const lastReportedRef = useRef<AttributionPayload | null>(null);
  // Refs below must be declared before the subscription effect: effects run in declaration order and
  // an already-known UID invokes its listener immediately.
  const reportRef = useRef<ReportAttribution>(async () => undefined);
  useEffect(() => {
    reportRef.current = async payload => {
      if (isSameAttribution(lastReportedRef.current, payload)) return;
      // Claim before the await so a second resolution arriving while this request is in flight sees
      // the claim and skips; release on failure so a later resolution (or retry) is not
      // permanently blocked by one failed attempt.
      lastReportedRef.current = payload;
      try {
        await reportAttribution(payload);
      } catch (error) {
        lastReportedRef.current = null;
        throw error;
      }
    };
  }, [reportAttribution]);

  const userRef = useRef<User | undefined>(undefined);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const lastPushedUidRef = useRef<string | null>(null);

  const prevUserIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (user && prevUserIdRef.current !== user.id) {
      lastReportedRef.current = null;
      lastPushedUidRef.current = null;
    }
    prevUserIdRef.current = user?.id;
  }, [user]);

  useEffect(() => {
    return onAppsFlyerUidResolved(uid => {
      const current = userRef.current;
      if (!current) return;
      void (async () => {
        try {
          if (lastPushedUidRef.current !== uid) {
            // Own try/catch: a RevenueCat failure must not prevent the attribution report
            // below from running.
            try {
              await Purchases.setAttributes({ $appsflyerId: uid });
              lastPushedUidRef.current = uid;
            } catch (error) {
              reportError('Failed to set RevenueCat appsflyer id', error);
            }
          }
          await reportAttributionIfParent(current, uid, payload => reportRef.current(payload));
        } catch (error) {
          reportError('AppsFlyer late UID handling error', error);
        }
      })();
    });
  }, []);

  useEffect(() => {
    if (!userType) {
      setIsParentUser(false);
      return;
    }

    const needsConfig = configuredRef.current.userType !== userType;
    const needsLogin = user && configuredRef.current.userId !== user.id;
    if (!needsConfig && !needsLogin) return;

    const currentVersion = ++versionRef.current;
    const isStale = () => currentVersion !== versionRef.current;

    const run = async () => {
      try {
        if (needsConfig) {
          await configureTracking(userType, isStale);
          if (isStale()) return;
          configuredRef.current.userType = userType;
        }
        if (user && configuredRef.current.userId !== user.id) {
          await loginUser(user, isStale);
          if (isStale()) return;
          configuredRef.current.userId = user.id;
        }
      } catch (error) {
        reportError('AppsFlyer initialization error', error);
      }
    };
    run();
  }, [userType, user]);

  return null;
};

export default AppsFlyer;
